import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarPlus } from 'lucide-react';
import { APPOINTMENT_SLOT_STARTS, type Appointment } from '@tailoring-crm/shared';
import { AppointmentFormDialog } from '../components/appointments/appointment-form-dialog.js';
import { AppointmentWeekGrid } from '../components/appointments/appointment-week-grid.js';
import { CancelAppointmentDialog } from '../components/appointments/cancel-appointment-dialog.js';
import { PageHeader } from '../components/layout/page-header.js';
import { Button, Input, Label, ListSkeleton, useToast } from '@tailoring-crm/ui';
import { LoadError } from '../components/feedback/load-error.js';
import {
  useAppointmentWeek,
  useSetAppointmentStatus,
} from '../lib/appointments/use-appointments.js';
import { dayKeyOf, mondayOf, shiftWeek, weekDays } from '../lib/appointments/week.js';
import { formatDate } from '../lib/format.js';
import { useStaffList } from '../lib/staff/use-staff.js';

/**
 * The screen a boutique opens to see its day.
 *
 * A week at a time, not a day and not a month: a shop books a fitting a few days out and needs
 * to see the days around it, but a full month of half-hour rows would not fit anything readable
 * on a phone. Booking, rescheduling, cancelling and closing a slot all happen from right here —
 * there is no separate "appointment detail" screen to navigate away to and lose the grid under.
 *
 * NO ROLE OR PERMISSION REFUSAL LIVES HERE, unlike the quotations screen. Task 14 is what
 * decides who may book, move or close a slot, and a guess made here could disagree with the
 * server's own answer — the honest thing is to let every request go and let the API say no.
 *
 * The customer search is CLIENT-SIDE, over the week already fetched. The question it answers —
 * "where does this person sit in this week?" — needs no new request, and the week query itself
 * carries no customer filter at all.
 */

interface FormDialogState {
  appointment: Appointment | null;
  prefill: { dayKey: string; startTime: string } | null;
}

export function AppointmentsPage(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [weekStart, setWeekStart] = useState(() => mondayOf(Date.now()));
  const [staffEmail, setStaffEmail] = useState('');
  const [customerQuery, setCustomerQuery] = useState('');
  const [formState, setFormState] = useState<FormDialogState | null>(null);
  const [cancelling, setCancelling] = useState<Appointment | null>(null);

  const staffList = useStaffList();
  const week = useAppointmentWeek({
    weekStart,
    ...(staffEmail ? { staffEmail } : {}),
  });
  const markComplete = useSetAppointmentStatus();

  const weekDaysList = useMemo(() => weekDays(weekStart), [weekStart]);

  const items = useMemo(() => week.data?.items ?? [], [week.data]);
  const filteredItems = useMemo(() => {
    const query = customerQuery.trim().toLowerCase();
    if (!query) return items;
    return items.filter((appointment) => appointment.customerName.toLowerCase().includes(query));
  }, [items, customerQuery]);

  const handleMarkComplete = (appointment: Appointment): void => {
    void (async () => {
      try {
        await markComplete.mutateAsync({
          appointmentId: appointment.id,
          input: { status: 'completed' },
        });
        toast({ title: t('appointments.toast.completed'), variant: 'success' });
      } catch (error) {
        toast({
          title: error instanceof Error ? error.message : t('error.unexpected'),
          variant: 'destructive',
        });
      }
    })();
  };

  const todayKey = dayKeyOf(Date.now());
  const defaultBookDay = weekDaysList.includes(todayKey) ? todayKey : weekStart;
  const firstSlot = APPOINTMENT_SLOT_STARTS[0] ?? '10:00';

  return (
    <>
      <PageHeader
        title={t('appointments.title')}
        description={t('appointments.description')}
        action={
          <Button
            onClick={() =>
              setFormState({
                appointment: null,
                prefill: { dayKey: defaultBookDay, startTime: firstSlot },
              })
            }
          >
            <CalendarPlus aria-hidden="true" />
            {t('appointments.bookButton')}
          </Button>
        }
      />

      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setWeekStart((current) => shiftWeek(current, -1))}
          >
            {t('appointments.prevWeek')}
          </Button>
          <span className="text-sm font-medium">
            {t('appointments.weekRange', {
              start: formatDate(weekDaysList[0] ?? weekStart),
              end: formatDate(weekDaysList[6] ?? weekStart),
            })}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setWeekStart((current) => shiftWeek(current, 1))}
          >
            {t('appointments.nextWeek')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setWeekStart(mondayOf(Date.now()))}>
            {t('appointments.thisWeek')}
          </Button>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="appointments-staff-filter">{t('appointments.staffLabel')}</Label>
            <select
              id="appointments-staff-filter"
              value={staffEmail}
              onChange={(event) => setStaffEmail(event.target.value)}
              className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
            >
              <option value="">{t('appointments.allStaff')}</option>
              {(staffList.data?.items ?? []).map((member) => (
                <option key={member.userId} value={member.email}>
                  {member.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor="appointments-customer-search">
              {t('appointments.customerSearchLabel')}
            </Label>
            <Input
              id="appointments-customer-search"
              type="search"
              value={customerQuery}
              onChange={(event) => setCustomerQuery(event.target.value)}
              placeholder={t('appointments.customerSearchPlaceholder')}
            />
          </div>
        </div>

        {week.isPending ? <ListSkeleton rows={8} /> : null}

        {/* A refused week must never render as a free one — that is how a full day gets
            double-booked. The grid is only drawn for a week the server actually answered. */}
        {week.isError ? <LoadError onRetry={() => void week.refetch()} /> : null}

        {!week.isPending && !week.isError ? (
          <AppointmentWeekGrid
            weekDays={weekDaysList}
            items={filteredItems}
            onSlotClick={(dayKey, startTime) =>
              setFormState({ appointment: null, prefill: { dayKey, startTime } })
            }
            onReschedule={(appointment) => setFormState({ appointment, prefill: null })}
            onCancel={setCancelling}
            onMarkComplete={handleMarkComplete}
          />
        ) : null}
      </div>

      <AppointmentFormDialog
        open={formState !== null}
        onOpenChange={(open) => !open && setFormState(null)}
        appointment={formState?.appointment ?? null}
        prefill={formState?.prefill ?? null}
      />

      <CancelAppointmentDialog
        appointment={cancelling}
        onOpenChange={(open) => !open && setCancelling(null)}
      />
    </>
  );
}

export default AppointmentsPage;
